import { useTheme } from 'next-themes';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import DarkMode from '@mui/icons-material/DarkModeOutlined';
import LightMode from '@mui/icons-material/LightModeOutlined';
import ContrastIcon from '@mui/icons-material/Contrast';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useFeatureAccess } from '@/hooks/use-feature-access';

type ThemeMode = 'light' | 'dark' | 'lowcontrast';

export default function ThemeSwitch() {
  const { setTheme, resolvedTheme } = useTheme();
  const { hasAccess: hasLowContrastAccess, isLoading: lowContrastAccessLoading } = useFeatureAccess('low-contrast');
  const showLowContrastOption = !lowContrastAccessLoading && hasLowContrastAccess;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeChange = (theme: ThemeMode) => {
    setTheme(theme);
    handleClose();
  };

  const getCurrentIcon = () => {
    switch (resolvedTheme as ThemeMode) {
      case 'dark':
        return <DarkMode />;
      case 'lowcontrast':
        return <ContrastIcon />;
      default:
        return <LightMode />;
    }
  };

  return (
    <>
      <IconButton
        data-testid="theme-switcher"
        onClick={handleClick}
        aria-controls={open ? 'theme-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {getCurrentIcon()}
      </IconButton>
      <Menu
        id="theme-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'theme-button',
        }}
      >
        <MenuItem onClick={() => handleThemeChange('light')}>
          <ListItemIcon>
            <LightMode fontSize="small" />
          </ListItemIcon>
          <ListItemText>Light</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleThemeChange('dark')}>
          <ListItemIcon>
            <DarkMode fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dark</ListItemText>
        </MenuItem>
        {showLowContrastOption && (
          <MenuItem onClick={() => handleThemeChange('lowcontrast')}>
            <ListItemIcon>
              <ContrastIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Low Contrast</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
